/**
 * Groups an array of items by a specified key
 *
 * @param items - Array of items to group
 * @param keyGetter - Function that returns the key to group by for each item
 * @returns An object with keys as group identifiers and values as arrays of items
 *
 * @example
 * // Group references by category
 * const groupedReferences = groupBy(references, (item) => item.category?.value);
 */
export function groupBy<T, K extends string | number | symbol>(
  items: T[],
  keyGetter: (item: T) => K,
): Record<string, T[]> {
  return items.reduce((acc: Record<string, T[]>, item: T) => {
    const key = String(keyGetter(item))
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(item)
    return acc
  }, {})
}

/**
 * Groups an array of items by a specified key and returns an array of group objects
 * formatted for use with React Select's grouped options
 *
 * @param items - Array of items to group
 * @param keyGetter - Function that returns the key to group by for each item
 * @returns An array of objects with label and options properties
 *
 * @example
 * // Group references by category into array format for React Select
 * const groupedReferences = groupByToArray(references,
 *   (item) => item.category?.label || 'Uncategorized'
 * );
 * // Result: [{ label: 'Category1', options: [...] }, { label: 'Category2', options: [...] }]
 */
export function groupByToArray<T, K extends string | number | symbol>(
  items: T[],
  keyGetter: (item: T) => K,
): Array<{ label: string; options: T[] }> {
  const grouped = groupBy(items, keyGetter)

  return Object.keys(grouped).map((key) => ({
    label: key,
    options: grouped[key],
  }))
}

/**
 * Orders a grouped array by the specified keys order
 *
 * @param groupedArray - Array of group objects returned by groupByToArray
 * @param order - Array of keys in the desired order
 * @returns An ordered array of group objects
 *
 * @example
 * // Order categories in a specific sequence
 * const orderedGroups = orderGroupedArray(groupedReferences, ['Category1', 'Category2', 'Uncategorized']);
 */
export function orderGroupedArray<T>(
  groupedArray: Array<{ label: string; options: T[] }>,
  order: string[],
): Array<{ label: string; options: T[] }> {
  // Create a map for quick lookup of order index
  const orderMap = order.reduce(
    (acc, key, index) => {
      acc[key] = index
      return acc
    },
    {} as Record<string, number>,
  )

  // Sort the grouped array based on the order
  return [...groupedArray].sort((a, b) => {
    const aIndex = orderMap[a.label] ?? Number.MAX_SAFE_INTEGER
    const bIndex = orderMap[b.label] ?? Number.MAX_SAFE_INTEGER
    return aIndex - bIndex
  })
}
